import { useEffect, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { ApiError, units as unitsApi, type UnitType } from '../api';
import { useAuth } from '../auth';
import { useUnits } from '../units';
import { InviteLink } from '../components/InviteLink';

/** SCO-16 — Platform Admin: provision units and assign a first admin. */
export function PlatformUnits() {
  const { me } = useAuth();
  const { reload } = useUnits();
  const [all, setAll] = useState<{ id: string; name: string; type: UnitType; createdAt: string }[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  function load() {
    setError(null);
    unitsApi
      .listAll()
      .then(setAll)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load'));
  }

  useEffect(() => {
    if (me?.isPlatformAdmin) load();
  }, [me]);

  if (!me?.isPlatformAdmin) return <Navigate to="/" replace />;

  return (
    <div className="panel">
      <h1>All units</h1>
      <p className="muted">Platform Admin — provisioning only. You get no standing access to unit data.</p>
      {error && <p className="error">{error}</p>}

      <CreateUnitForm
        onCreated={() => {
          load();
          void reload();
        }}
      />

      <table className="grid">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {all?.map((u) => (
            <tr key={u.id}>
              <td>{u.name}</td>
              <td>{u.type === 'PACK' ? 'Pack' : 'Troop'}</td>
              <td className="muted">{new Date(u.createdAt).toLocaleDateString()}</td>
            </tr>
          ))}
          {all?.length === 0 && (
            <tr>
              <td colSpan={3} className="muted">
                No units yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function CreateUnitForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('');
  const [type, setType] = useState<UnitType>('PACK');
  const [firstAdminEmail, setFirstAdminEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInviteUrl(null);
    setBusy(true);
    try {
      const res = await unitsApi.create({
        name,
        type,
        firstAdminEmail: firstAdminEmail.trim() || undefined,
      });
      setName('');
      setFirstAdminEmail('');
      setType('PACK');
      if (res.invite) setInviteUrl(res.invite.acceptUrl);
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create unit');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <form className="create-unit" onSubmit={submit}>
        <input
          placeholder="Unit name (e.g. Pack 314)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <select value={type} onChange={(e) => setType(e.target.value as UnitType)}>
          <option value="PACK">Pack</option>
          <option value="TROOP">Troop</option>
        </select>
        <input
          type="email"
          placeholder="First admin email (optional)"
          value={firstAdminEmail}
          onChange={(e) => setFirstAdminEmail(e.target.value)}
        />
        <button type="submit" className="primary" disabled={busy || !name.trim()}>
          {busy ? '…' : 'Create unit'}
        </button>
        {error && <span className="error">{error}</span>}
      </form>
      {inviteUrl && (
        <InviteLink url={inviteUrl} label="Unit created. That admin has no account yet — send them this link:" />
      )}
    </>
  );
}
